<template>
  <div>
    <div v-if="hasItlModule">
      <subtitle class="mb-3 mt-8">
        {{ $t("add_feedback.rpe_training_grade_by_player") }}
      </subtitle>

      <validation-observer ref="validationObserver">
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-slider
            ref="sliderInput"
            v-model="dirtyFeedbackForm.trainingGrade"
            :color="!dirtyFeedbackForm.trainingGrade ? undefined : resolveJointColor(dirtyFeedbackForm.trainingGrade)"
            :thumb-color="!dirtyFeedbackForm.trainingGrade ? undefined : resolveJointColor(dirtyFeedbackForm.trainingGrade)"
            :readonly="disabled"
            track-color="grey"
            thumb-label="always"
            ticks="always"
            :step="0.5"
            :tick-size="4"
            :min="1"
            :max="10"
            :error-messages="errors"
            dark
            class="pt-12"
          >
            <template v-slot:append>
              <v-chip
                small
                label
                dark
                color="#D13D4B"
                @click="!disabled ? dirtyFeedbackForm.trainingGrade = 10 : undefined"
              >
                10
              </v-chip>
            </template>
            <template v-slot:prepend>
              <v-chip
                small
                label
                dark
                color="#86CC8E"
                @click="!disabled ? dirtyFeedbackForm.trainingGrade = 1 : undefined"
              >
                1
              </v-chip>
            </template>
          </v-slider>
        </validation-provider>
      </validation-observer>

      <subtitle class="mb-3 mt-8">
        {{ $t("add_feedback.pain") }}
      </subtitle>

      <v-row>
        <v-col class="justify-center d-flex">
          <human-body-female-front-muscles
            v-if="player.gender === 'female'"
            style="height: 400px; width: 220px"
            :stats="dirtyFeedbackForm.pain"
            interactive
            @click="muscleToggle"
          />
          <human-body-front-muscles
            v-else
            style="height: 400px; width: 220px"
            :stats="dirtyFeedbackForm.pain"
            interactive
            @click="muscleToggle"
          />
        </v-col>
        <v-col class="justify-center d-flex">
          <human-body-female-back-muscles
            v-if="player.gender === 'female'"
            style="height: 400px; width: 220px"
            :stats="dirtyFeedbackForm.pain"
            interactive
            @click="muscleToggle"
          />
          <human-body-back-muscles
            v-else
            style="height: 400px; width: 220px"
            :stats="dirtyFeedbackForm.pain"
            interactive
            @click="muscleToggle"
          />
        </v-col>
      </v-row>

      <subtitle class="mb-3 mt-12">
        {{ $t("add_feedback.how_enjojable") }}
      </subtitle>

      <v-slider
        ref="sessionEnjoymentSliderInput"
        v-model="dirtyFeedbackForm.sessionEnjyoment"
        :color="!dirtyFeedbackForm.sessionEnjyoment ? undefined : resolveGradeColor(dirtyFeedbackForm.sessionEnjyoment)"
        :thumb-color="!dirtyFeedbackForm.sessionEnjyoment ? undefined : resolveGradeColor(dirtyFeedbackForm.sessionEnjyoment)"
        :readonly="disabled"
        track-color="grey"
        thumb-label="always"
        ticks="always"
        :step="1"
        :tick-size="4"
        :min="1"
        :max="5"
        dark
        class="pt-12"
      >
        <template v-slot:append>
          <v-chip
            small
            label
            dark
            color="#86CC8E"
            @click="!disabled ? dirtyFeedbackForm.sessionEnjyoment = 5: undefined"
          >
            5
          </v-chip>
        </template>
        <template v-slot:prepend>
          <v-chip
            small
            label
            dark
            color="#D13D4B"
            @click="!disabled ? dirtyFeedbackForm.sessionEnjyoment = 1: undefined"
          >
            1
          </v-chip>
        </template>
      </v-slider>
    </div>
    <div
      v-else
      class="text-body-1"
    >
      <v-row align="center">
        <v-col cols="3">
          {{ $t('individual_analysis.event_attendance') }}
        </v-col>
        <v-col>
          <v-btn-toggle
            v-model="dirtyFeedbackForm.hasAttended"
            dense
          >
            <v-btn
              class="text-none"
              :value="true"
              outlined
              :color="dirtyFeedbackForm.hasAttended === true ? 'success' : undefined"
            >
              {{ $t('attended.label') }}
            </v-btn>
            <v-btn
              class="text-none"
              :value="false"
              outlined
              :color="dirtyFeedbackForm.hasAttended === false ? 'error' : undefined"
            >
              {{ $t('card_title.did_not_attend') }}
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
      <v-row v-if="dirtyFeedbackForm.hasAttended === false">
        <v-col
          cols="3"
          class="pt-5"
        >
          {{ $t('select_reason.dialog.header') }}
        </v-col>
        <v-col cols="5">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-select
              v-model="dirtyFeedbackForm.eventMissedReasonId"
              :items="eventMissedReasons"
              item-value="id"
              item-text="reason"
              outlined
              dense
              :placeholder="$t('dropdown.select.placeholder')"
              :error-messages="errors"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import Subtitle from '@/components/helpers/Subtitle.vue'
import HumanBodyFrontMuscles from '@/components/human-body/HumanBodyFrontMuscles.vue'
import HumanBodyBackMuscles from '@/components/human-body/HumanBodyBackMuscles.vue'
import HumanBodyFemaleFrontMuscles from '@/components/human-body/HumanBodyFemaleFrontMuscles.vue'
import HumanBodyFemaleBackMuscles from '@/components/human-body/HumanBodyFemaleBackMuscles.vue'
import { getColorForValue } from '@/utils/helpers'

export default {
  name: 'FeedbackInput',
  components: { HumanBodyFemaleBackMuscles, HumanBodyFemaleFrontMuscles, HumanBodyBackMuscles, HumanBodyFrontMuscles, Subtitle },
  props: {
    feedbackForm: {
      type: Object,
      default: () => ({})
    },
    player: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    eventMissedReasons: {
      type: Array,
      default: () => ([])
    }
  },
  data() {
    return {
    }
  },
  computed: {
    dirtyFeedbackForm: {
      get() {
        return this.feedbackForm
      },
      set(value) {
        this.$emit('update:feedbackForm', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module || false
    }
  },
  created() {
  },
  methods: {
    resolveJointColor(i) {
      return getColorForValue(i, 10, true)
    },
    resolveGradeColor(i) {
      return getColorForValue(i, 5)
    },
    muscleToggle(muscle) {
      if (this.disabled) return

      this.dirtyFeedbackForm.pain[muscle] = this.dirtyFeedbackForm.pain[muscle] === 3 ? 0 : (this.dirtyFeedbackForm.pain[muscle] + 1)
    }
  }

}
</script>
